import { useEffect, useState } from "react";
import { AppLog } from "services";
import { useAppSelector } from "store/store";
import { RequestApi, RouteApi, StatusMsg } from "utils";
import { toast } from "react-toastify";
import { Button } from "components/ui/button";
import { useLocation } from "react-router-dom";
import { RadioGroup, RadioGroupItem } from "components/ui/radio-group";
import { Label } from "components/ui/label";
import clsx from "clsx";
import dayjs from "dayjs";
import { Check, CircleAlert, CircleCheckBig, Info, LoaderCircle, TriangleAlert } from "lucide-react";
import { Skeleton } from "components/ui/skeleton";
import { Service } from "utils/services";

function getFirstTimesheetEntries(employeeShift: any) {
  if (!employeeShift?.clock_times) return null;

  const sorted = [...employeeShift.clock_times].sort((a, b) => a.id - b.id);

  const result = {
    employee_time_in: null,
    employee_break_in: null,
    employee_break_out: null,
    employee_time_out: null,
  };

  for (const item of sorted) {
    switch (item.working_mode) {
      case "Time In":
        if (!result.employee_time_in) result.employee_time_in = item;
        break;
      case "Break In":
        if (!result.employee_break_in) result.employee_break_in = item;
        break;
      case "Break Out":
        if (!result.employee_break_out) result.employee_break_out = item;
        break;
      case "Time Out":
        if (!result.employee_time_out) result.employee_time_out = item;
        break;
    }
  }

  return result;
}

export default function EssAttendance() {
  const { token } = useAppSelector((state) => state.auth);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const shopId = searchParams.get("id");
  const shiftId = searchParams.get("shift_id");
  const [employeeShift, setemployeeShift] = useState<any>(null);
  const [loadingEmployeeShift, setloadingEmployeeShift] = useState<boolean>(false);
  const [loadingSubmitEmployeeShift, setloadingSubmitEmployeeShift] = useState<boolean>(false);
  const [nextPage, setNextPage] = useState<{ isNextPage: boolean; message: string; status: string }>({
    isNextPage: false,
    message: "",
    status: "",
  });
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [selectedLabel, setSelectedLabel] = useState("");
  const currentTime = dayjs().format("HH:mm:ss");
  const [employeeTimesheet, setEmployeeTimesheet] = useState<any>({
    employee_time_in: null,
    employee_break_in: null,
    employee_break_out: null,
    employee_time_out: null,
  });

  const initGetEssAttendance = async () => {
    setloadingEmployeeShift(true);
    try {
      let url: string = "";
      url = RouteApi.getEssShopShift;
      url += `token=${token}`;

      const data = {
        shop_id: shopId,
        shift_id: shiftId,
        latitude: userLocation ? userLocation?.lat : "",
        longitude: userLocation ? userLocation?.lng : "",
      };

      const response = await RequestApi(url, { data });

      if (response.status === "success") {
        setemployeeShift(response);
      } else {
        AppLog.show({ message: response.msg, status: StatusMsg.getType(response.status) });
      }
    } catch (error) {
      AppLog.show({
        message: `[initGetEssAttendance]`,
        status: StatusMsg.error,
        error,
      });
    } finally {
      setloadingEmployeeShift(false);
    }
  };

  const handleSubmit = async () => {
    if (selectedLabel) {
      setloadingSubmitEmployeeShift(true);
      try {
        let url: string = "";
        url = RouteApi.submitUploadClockTime;
        url += `token=${token}`;

        const data = {
          shop_id: shopId,
          records: [
            {
              enroll_no: employeeShift?.employee_timesheet?.attendance_card_no,
              working_type: "Normal",
              working_mode: selectedLabel,
              working_date: employeeShift?.employee_timesheet?.working_date,
              working_time: currentTime,
              working_datetime: `${employeeShift?.employee_timesheet?.working_date} ${currentTime}`,
              latitude: userLocation ? userLocation?.lat : "",
              longitude: userLocation ? userLocation?.lng : "",
            },
          ],
        };

        const response = await RequestApi(url, { data });

        if (response.status === "success") {
          setNextPage({ isNextPage: true, message: response.msg, status: response.status });
        } else {
          setNextPage({ isNextPage: true, message: response.msg, status: response.status });
        }
      } catch (error) {
        AppLog.show({
          message: `[initGetEssAttendance]`,
          status: StatusMsg.error,
          error,
        });
      } finally {
        setloadingSubmitEmployeeShift(false);
      }
    } else {
      toast.warning("Please select your option first.");
    }
  };

  const renderRadioOption = (label: string, field: keyof typeof employeeTimesheet) => (
    <div>
      <RadioGroupItem value={label} id={label} className="sr-only peer" />
      <Label
        htmlFor={label}
        className={clsx(
          "group relative flex items-center justify-between w-full px-6 py-4 rounded-xl border-2 transition-all duration-300 ease-in-out cursor-pointer shadow-sm",
          selectedLabel === label ? "border-yellow-500" : "border-gray-300 dark:border-gray-600 dark:hover:bg-gray-700"
        )}
      >
        <div className="flex flex-col">
          <span className="text-lg font-bold">{label}</span>
          <span className="text-sm mt-1">{Service.formatTime(employeeTimesheet?.[field]?.working_time) || "..."}</span>
        </div>
        {employeeTimesheet?.[field] && (
          <span className="flex justify-center items-center bg-green-600 text-white w-10 h-10 px-2 py-0.5 rounded-full">
            <Check className="w-6 h-6" strokeWidth={3} />
          </span>
        )}
      </Label>
    </div>
  );

  useEffect(() => {
    if (shopId && shiftId) {
      initGetEssAttendance();
    }
  }, [shopId, shiftId, userLocation]);

  useEffect(() => {
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
          console.log("position", position);
        },
        (error) => {
          console.log("Geo location ERROR :", error);
        }
      );
    } else {
      console.log("Geolocation is not supported by your browser.");
    }
  }, []);

  // useEffect(() => {
  //   fetch("https://ipapi.co/json/")
  //     .then((res) => res.json())
  //     .then((data) => {
  //       setUserLocation({
  //         lat: data.latitude,
  //         lng: data.longitude,
  //       });
  //     })
  //     .catch((err) => console.error(err));
  // }, []);

  useEffect(() => {
    if (employeeShift) {
      const timesheet = getFirstTimesheetEntries(employeeShift);
      setEmployeeTimesheet(
        timesheet || {
          employee_time_in: null,
          employee_break_in: null,
          employee_break_out: null,
          employee_time_out: null,
        }
      );
    }
  }, [employeeShift]);

  console.log("userLocation", userLocation);

  return (
    <>
      {loadingSubmitEmployeeShift && (
        <div className="w-full h-full bg-black bg-opacity-70 flex justify-center absolute items-center z-40">
          <LoaderCircle size={30} className="animate-spin text-yellow-400" />
        </div>
      )}
      {nextPage.isNextPage ? (
        <div className="flex justify-center items-center h-screen">
          <div className="mb-8 p-4 text-center">
            {nextPage.status === "success" && (
              <>
                <CircleCheckBig className="mx-auto w-[100px] h-[100px] text-green-500 mb-6" />
                <p className="font-semibold">{nextPage.message}</p>
              </>
            )}
            {nextPage.status === "warning" && (
              <>
                <CircleAlert className="text-yellow-500 mx-auto w-[100px] h-[100px] mb-6" />
                <p className="font-semibold">{nextPage.message}</p>
              </>
            )}
            {nextPage.status === "error" && (
              <>
                <TriangleAlert className="text-red-600 mx-auto w-[100px] h-[100px] mb-6" />
                <p className="font-semibold">{nextPage.message}</p>
              </>
            )}
            {nextPage.status === "info" && (
              <>
                <Info className="text-yellow-500 mx-auto w-[100px] h-[100px] mb-6" />
                <p className="font-semibold">{nextPage.message}</p>
              </>
            )}
          </div>
        </div>
      ) : (
        <div className="p-4">
          <h1 className="font-semibold text-xl text-center pt-4">Choose Check In/Out Time</h1>
          <div className="space-y-4 h-[calc(100vh-80px)] flex justify-center">
            <div className="w-full xs:w-1/3">
              {loadingEmployeeShift ? (
                <div className="flex flex-col justify-between w-full h-full pt-6">
                  <div className="space-y-4">
                    <div className="space-y-3">
                      {[...Array(4)].map((_, index) => {
                        return <div key={index}>{<Skeleton className="w-full h-24" />}</div>;
                      })}
                    </div>
                  </div>
                  <div className="mt-4">
                    <Skeleton className="w-full h-12" />
                  </div>
                </div>
              ) : (
                <div className="flex flex-col justify-between w-full h-full pt-6">
                  <div className="space-y-4">
                    <RadioGroup
                      value={selectedLabel}
                      onValueChange={(value) => {
                        setSelectedLabel(value);
                      }}
                      className="space-y-3"
                    >
                      {renderRadioOption("Time In", "employee_time_in")}
                      {renderRadioOption("Break Out", "employee_break_out")}
                      {renderRadioOption("Break In", "employee_break_in")}
                      {renderRadioOption("Time Out", "employee_time_out")}
                    </RadioGroup>
                  </div>
                  <div className="mt-4">
                    <Button onClick={handleSubmit} className="w-full text-white">
                      Done
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
